import { Text, tokens } from '@hotjar/hotsauce';

import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { ListButton } from './ListButton';
import type { EventEmitter, NavigationProps } from '@hotjar/navigation/src/types';

import { Position } from '@hotjar/navigation/src/events/Position';

import { useNavigation } from '@hotjar/navigation/src/hooks/useNavigation';

export interface SettingsSidebarListProps {
  emitter: EventEmitter;
  isCollapsed: boolean;
  currentRouteConfig?: NavigationProps['currentRouteConfig'];
  sidebarNotifications?: NavigationProps['sidebarNotifications'];
  showPreferences?: boolean;
  showInvoices?: boolean;
  showSubscriptions?: boolean;
  showPlansAndBilling?: boolean;
  showPublicApi?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${tokens.spacer.x0};
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  gap: ${tokens.spacer.x0};
  display: flex;
  flex-direction: column;
`;

export const SettingsSidebarList = ({
  emitter,
  isCollapsed,
  currentRouteConfig,
  sidebarNotifications,
  showPreferences,
  showInvoices,
  showSubscriptions,
  showPlansAndBilling,
  showPublicApi,
}: SettingsSidebarListProps): JSX.Element => {
  const { t } = useTranslation('navigation');
  const { routes, buildHrefFromRoute } = useNavigation();
  return (
    <Container aria-label="Settings menu">
      {!isCollapsed && (
        <Text
          textType="subtitle2"
          textColor="text_light"
          css={css`
            margin: ${tokens.spacer.x1} 0;
          `}
        >
          {t('SettingsMenu.account')}
        </Text>
      )}
      <List>
        <li>
          <ListButton
            text={t('SettingsMenu.organizationsAndSites')}
            icon="building_business"
            isCollapsed={isCollapsed}
            isActive={currentRouteConfig?.path?.startsWith(routes.SITE.BASE.path)}
            notificationLabel={sidebarNotifications?.organizationsAndSites}
            path={buildHrefFromRoute(routes.SITE.LIST.path)}
            onClick={() => {
              emitter.emit('clickedOrgsAndSites', { position: Position.accountSettingsToolbar });
            }}
          />
        </li>
        <li>
          <ListButton
            text={t('SettingsMenu.team')}
            icon="user_multiple"
            isCollapsed={isCollapsed}
            isActive={currentRouteConfig?.path?.startsWith(routes.TEAM.BASE.path)}
            notificationLabel={sidebarNotifications?.team}
            path={buildHrefFromRoute(routes.TEAM.LIST.path)}
            onClick={() => {
              emitter.emit('clickedTeam', { position: Position.accountSettingsToolbar });
            }}
          />
        </li>
        <li>
          <ListButton
            text={t('SettingsMenu.userAttributes')}
            icon="user_attribute"
            isCollapsed={isCollapsed}
            isActive={currentRouteConfig?.path?.startsWith(routes.SETTINGS.USER_ATTRIBUTES.path)}
            notificationLabel={sidebarNotifications?.userAttributes}
            path={buildHrefFromRoute(routes.SETTINGS.USER_ATTRIBUTES.path)}
            onClick={() => {
              emitter.emit('clickedUserAttributes', { position: Position.accountSettingsToolbar });
            }}
          />
        </li>
        <li>
          <ListButton
            text={t('SettingsMenu.integrations')}
            icon="puzzle"
            isCollapsed={isCollapsed}
            isActive={currentRouteConfig?.path?.startsWith(routes.SETTINGS.INTEGRATIONS.path)}
            notificationLabel={sidebarNotifications?.integrations}
            path={buildHrefFromRoute(routes.SETTINGS.INTEGRATIONS.path)}
            onClick={() => {
              emitter.emit('clickedIntegrations', { position: Position.accountSettingsToolbar });
            }}
          />
        </li>
        <li>
          <ListButton
            text={t('SettingsMenu.userLookup')}
            icon="book_address"
            isCollapsed={isCollapsed}
            isActive={currentRouteConfig?.path?.startsWith(routes.SETTINGS.USER_LOOKUP.path)}
            notificationLabel={sidebarNotifications?.userLookup}
            path={buildHrefFromRoute(routes.SETTINGS.USER_LOOKUP.path)}
            onClick={() => {
              emitter.emit('clickedUserLookup', { position: Position.accountSettingsToolbar });
            }}
          />
        </li>
        {showPreferences && (
          <li>
            <ListButton
              text={t('SettingsMenu.preferences')}
              icon="toggles"
              isCollapsed={isCollapsed}
              isActive={currentRouteConfig?.path?.startsWith(routes.SETTINGS.ACCOUNT.path)}
              notificationLabel={sidebarNotifications?.preferences}
              path={buildHrefFromRoute(routes.SETTINGS.ACCOUNT.path)}
              onClick={() => {
                emitter.emit('clickedPreferences', { position: Position.accountSettingsToolbar });
              }}
            />
          </li>
        )}
        <li>
          <ListButton
            text={t('SettingsMenu.singleSignOn')}
            icon="lock"
            isCollapsed={isCollapsed}
            isActive={currentRouteConfig?.path?.startsWith(routes.SSO.BASE.path)}
            notificationLabel={sidebarNotifications?.singleSignOn}
            path={buildHrefFromRoute(routes.SSO.BASE.path)}
            onClick={() => {
              emitter.emit('clickedSSO', { position: Position.accountSettingsToolbar });
            }}
          />
        </li>
        {showPublicApi && (
          <li>
            <ListButton
              text={t('SettingsMenu.api')}
              icon="code"
              isCollapsed={isCollapsed}
              path={buildHrefFromRoute(routes.SETTINGS.PUBLIC_API.path)}
              onClick={() => {
                emitter.emit('clickedSSO', { position: Position.accountSettingsToolbar });
              }}
            />
          </li>
        )}
      </List>
      {!isCollapsed && (
        <Text
          textType="subtitle2"
          textColor="text_light"
          css={css`
            margin: ${tokens.spacer.x1} 0;
          `}
        >
          {t('SettingsMenu.billing')}
        </Text>
      )}
      <List>
        {showPlansAndBilling && (
          <li>
            <ListButton
              text={t('SettingsMenu.plansAndBilling')}
              icon="user_multiple"
              isCollapsed={isCollapsed}
              isActive={currentRouteConfig?.path?.startsWith(routes.SETTINGS.BILLING.path)}
              notificationLabel={sidebarNotifications?.plansAndBilling}
              path={buildHrefFromRoute(routes.SETTINGS.BILLING.path)}
              onClick={() => {
                emitter.emit('clickedPlansBilling', { position: Position.accountSettingsToolbar });
              }}
            />
          </li>
        )}
        {showSubscriptions && (
          <>
            <li>
              <ListButton
                text={t('SettingsMenu.subscriptions')}
                icon="building_business"
                isCollapsed={isCollapsed}
                isActive={currentRouteConfig?.path?.startsWith(routes.SETTINGS.SUBSCRIPTIONS.path)}
                notificationLabel={sidebarNotifications?.subscriptions}
                path={buildHrefFromRoute(routes.SETTINGS.SUBSCRIPTIONS.path)}
                onClick={() => {
                  emitter.emit('clickedSubscriptions', {
                    position: Position.accountSettingsToolbar,
                  });
                }}
              />
            </li>
            <li>
              <ListButton
                text={t('SettingsMenu.invoices')}
                icon="receipt"
                isCollapsed={isCollapsed}
                isActive={currentRouteConfig?.path?.startsWith(routes.SETTINGS.INVOICES.path)}
                notificationLabel={sidebarNotifications?.invoices}
                path={buildHrefFromRoute(routes.SETTINGS.INVOICES.path)}
                onClick={() => {
                  emitter.emit('clickedInvoices', { position: Position.accountSettingsToolbar });
                }}
              />
            </li>
            <li>
              <ListButton
                text={t('SettingsMenu.paymentDetails')}
                icon="billing_card"
                isCollapsed={isCollapsed}
                isActive={currentRouteConfig?.path?.startsWith(
                  routes.SETTINGS.PAYMENT_DETAILS.path,
                )}
                notificationLabel={sidebarNotifications?.paymentDetails}
                path={buildHrefFromRoute(routes.SETTINGS.PAYMENT_DETAILS.path)}
                onClick={() => {
                  emitter.emit('clickedPaymentDetails', {
                    position: Position.accountSettingsToolbar,
                  });
                }}
              />
            </li>
          </>
        )}
        {showInvoices && !showSubscriptions && (
          <li>
            <ListButton
              text={t('SettingsMenu.invoices')}
              icon="user_attribute"
              isCollapsed={isCollapsed}
              isActive={currentRouteConfig?.path?.startsWith(routes.SETTINGS.INVOICES.path)}
              notificationLabel={sidebarNotifications?.invoices}
              path={buildHrefFromRoute(routes.SETTINGS.INVOICES.path)}
              onClick={() => {
                emitter.emit('clickedInvoices', { position: Position.accountSettingsToolbar });
              }}
            />
          </li>
        )}
      </List>
    </Container>
  );
};
